import React, { memo, useCallback, useMemo, useRef, useState } from 'react';

import { Download, TableChart } from '@mui/icons-material';
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { downloadCsvFile } from '~/utils/csvDownload';

import DateRangeSlider from './DateRangeSlider';
import SupplierFilterChips from './SupplierFilterChips';
import { recomputeAnalysis } from './analyzeParityCsv';
import { BEST_COMPETITOR_LABEL, LE_SUPPLIER_LABEL } from './constants';
import { downloadDomElementAsPng } from './downloadDomAsPng';
import { MatrixDisplayMode, ParityAnalysis, TRACKED_OTAS } from './types';
import { useParityReportDateRange } from './useParityReportDateRange';
import {
  buildMonthGroups,
  buildSupplierPriceMap,
  deriveLowestPriceByDate,
  deriveVisibleOtas,
  fmtShortDate,
  formatMatrixCellDisplay,
  getHeatmapColors,
  matrixCellExportValue,
} from './utils';

interface Props {
  analysis: ParityAnalysis;
}

function CalendarRateView({ analysis }: Props) {
  const [hiddenSuppliers, setHiddenSuppliers] = useState<Set<string>>(new Set());
  const [matrixDisplayMode, setMatrixDisplayMode] = useState<MatrixDisplayMode>('absolute');

  const activeCalendarOtas = useMemo(() => deriveVisibleOtas(hiddenSuppliers), [hiddenSuppliers]);

  const calendarAnalysis = useMemo(
    () => recomputeAnalysis(analysis, activeCalendarOtas),
    [analysis, activeCalendarOtas],
  );

  const dates = useMemo(() => calendarAnalysis.dailyDetail.map((d) => d.date), [calendarAnalysis.dailyDetail]);
  const totalDays = dates.length;
  const { dateRange, setDateRange } = useParityReportDateRange(totalDays, analysis);

  const visibleDailyDetail = useMemo(
    () => calendarAnalysis.dailyDetail.slice(dateRange[0], dateRange[1] + 1),
    [calendarAnalysis.dailyDetail, dateRange],
  );
  const visibleDates = useMemo(() => dates.slice(dateRange[0], dateRange[1] + 1), [dates, dateRange]);

  const supplierPrices = useMemo(() => buildSupplierPriceMap(visibleDailyDetail), [visibleDailyDetail]);

  const monthGroups = useMemo(() => buildMonthGroups(visibleDailyDetail), [visibleDailyDetail]);

  const visibleRows = useMemo(() => {
    const otas: Array<string> = [...TRACKED_OTAS].filter((ota) => !hiddenSuppliers.has(ota));
    const rows = [...otas, LE_SUPPLIER_LABEL];
    if (!hiddenSuppliers.has(BEST_COMPETITOR_LABEL)) {
      rows.push(BEST_COMPETITOR_LABEL);
    }
    return rows;
  }, [hiddenSuppliers]);

  const toggleSupplier = (name: string) => {
    setHiddenSuppliers((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const lowestPriceByDate = useMemo(() => deriveLowestPriceByDate(visibleDailyDetail), [visibleDailyDetail]);

  const getCellHeatmap = (supplier: string, date: string) => {
    const price = supplierPrices[supplier]?.[date] ?? null;
    const lowestPrice = lowestPriceByDate[date] ?? null;
    return getHeatmapColors(price, lowestPrice);
  };

  const handleDownloadCsv = useCallback(() => {
    const allSuppliers = [...TRACKED_OTAS, LE_SUPPLIER_LABEL, BEST_COMPETITOR_LABEL];
    const dateLabels = visibleDates.map((d) => fmtShortDate(d));
    const headers = ['Supplier', ...dateLabels];
    const rows: Array<Record<string, unknown>> = allSuppliers.map((supplier) => {
      const row: Record<string, unknown> = { Supplier: supplier };
      visibleDates.forEach((date, i) => {
        const price = supplierPrices[supplier]?.[date] ?? null;
        const lowestPrice = lowestPriceByDate[date] ?? null;
        const v = matrixCellExportValue(matrixDisplayMode, price, lowestPrice);
        row[dateLabels[i]] = v != null ? v : '';
      });
      return row;
    });
    downloadCsvFile(rows, headers, `parity_matrix_${analysis.currency}_${visibleDates[0] ?? 'unknown'}.csv`);
  }, [visibleDates, supplierPrices, lowestPriceByDate, matrixDisplayMode, analysis.currency]);

  const tableRef = useRef<HTMLDivElement>(null);

  const handleShareScreenshot = useCallback(async () => {
    if (!tableRef.current) {
      return;
    }
    await downloadDomElementAsPng(
      tableRef.current,
      `parity_matrix_${analysis.currency}_${visibleDates[0] ?? 'unknown'}.png`,
    );
  }, [analysis.currency, visibleDates]);

  const stickyColumnSx = {
    position: 'sticky' as const,
    left: 0,
    zIndex: 1,
    bgcolor: 'background.paper',
    whiteSpace: 'nowrap',
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 24, fontWeight: 600 }}>Competitive Calendar Matrix</Typography>
        <Box>
          <IconButton onClick={handleShareScreenshot} title="Download snapshot">
            <Download />
          </IconButton>
          <IconButton onClick={handleDownloadCsv} title="Download CSV">
            <TableChart />
          </IconButton>
        </Box>
      </Stack>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="calendar-matrix-display-label">Show as</InputLabel>
          <Select<MatrixDisplayMode>
            labelId="calendar-matrix-display-label"
            value={matrixDisplayMode}
            label="Show as"
            onChange={(e) => setMatrixDisplayMode(e.target.value as MatrixDisplayMode)}
          >
            <MenuItem value="absolute">Absolute price</MenuItem>
            <MenuItem value="priceDiff">Price difference</MenuItem>
            <MenuItem value="percentDiff">Percentage difference</MenuItem>
          </Select>
        </FormControl>
        <SupplierFilterChips
          suppliers={TRACKED_OTAS}
          isActive={(ota) => !hiddenSuppliers.has(ota)}
          onToggle={toggleSupplier}
          label="Filter Suppliers:"
          showLeChip
          showBestCompetitorChip
          isBestCompetitorActive={!hiddenSuppliers.has(BEST_COMPETITOR_LABEL)}
          onToggleBestCompetitor={() => toggleSupplier(BEST_COMPETITOR_LABEL)}
        />
      </Box>

      <TableContainer ref={tableRef} component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  ...stickyColumnSx,
                  zIndex: 3,
                  bgcolor: 'grey.100',
                }}
              />
              {monthGroups.map((group) => (
                <TableCell
                  key={group.month}
                  colSpan={group.count}
                  align="center"
                  sx={{
                    bgcolor: 'grey.100',
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                  }}
                >
                  {group.month}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell
                sx={{
                  ...stickyColumnSx,
                  zIndex: 3,
                  bgcolor: 'grey.100',
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                }}
              />
              {visibleDates.map((date) => (
                <TableCell
                  key={date}
                  align="center"
                  sx={{
                    bgcolor: 'grey.100',
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {fmtShortDate(date)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((supplier) => (
              <TableRow key={supplier}>
                <TableCell
                  sx={{
                    ...stickyColumnSx,
                    fontWeight: 600,
                  }}
                >
                  {supplier}
                </TableCell>
                {visibleDates.map((date) => {
                  const price = supplierPrices[supplier]?.[date] ?? null;
                  const lowestPrice = lowestPriceByDate[date] ?? null;
                  const displayText = formatMatrixCellDisplay(matrixDisplayMode, analysis.currency, price, lowestPrice);
                  const heatmap = getCellHeatmap(supplier, date);
                  return (
                    <TableCell
                      key={date}
                      align="right"
                      sx={{
                        py: 2,
                        px: 1,
                        fontSize: 14,
                        fontWeight: 600,
                        border: 1,
                        borderColor: 'divider',
                      }}
                    >
                      {heatmap ? (
                        <Box
                          component="span"
                          sx={{
                            ...heatmap,
                            borderRadius: '6px',
                            px: 1,
                            py: 0.5,
                          }}
                        >
                          {displayText}
                        </Box>
                      ) : (
                        displayText
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {totalDays > 1 && (
        <DateRangeSlider dates={dates} range={[dateRange[0], dateRange[1]]} onRangeChange={setDateRange} />
      )}
    </Box>
  );
}

export default memo(CalendarRateView);
